import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAdminNotificationStore,
  notificationModule,
  notificationLink,
  type AdminNotification,
} from '../../store/adminNotificationStore';
import { Card, CardHeader, CardContent } from '../../components/Card';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import {
  Bell, CheckCheck, Search, Briefcase, Users, Calendar,
  UserPlus, UserMinus, ShieldCheck, Clock, FileText,
  AlertCircle, ExternalLink, Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORY_TABS = [
  { id: 'all', label: 'All Notifications' },
  { id: 'unread', label: 'Unread Only' },
  { id: 'people', label: 'People & Recruitment' },
  { id: 'work', label: 'Work & Projects' },
  { id: 'system', label: 'System & Security' },
];

const PEOPLE_MODULES = [
  'recruitment', 'candidates', 'interviews', 'onboarding', 'offboarding',
  'employees', 'leave', 'attendance', 'payroll', 'shifts',
];

const MODULE_LABELS: Record<string, string> = {
  recruitment: 'Recruitment',
  candidates: 'Candidates',
  interviews: 'Interviews',
  onboarding: 'Onboarding',
  offboarding: 'Offboarding',
  employees: 'Directory',
  leave: 'Leave',
  attendance: 'Attendance',
  payroll: 'Payroll',
  shifts: 'Shifts',
  work: 'Work',
  support: 'Support',
  system: 'System',
};

function getModuleIcon(module: string) {
  switch (module) {
    case 'recruitment':
      return <Briefcase className="size-4 text-primary" />;
    case 'candidates':
      return <Users className="size-4 text-info" />;
    case 'interviews':
      return <Calendar className="size-4 text-warning" />;
    case 'onboarding':
      return <UserPlus className="size-4 text-success" />;
    case 'offboarding':
      return <UserMinus className="size-4 text-destructive" />;
    case 'employees':
      return <Users className="size-4 text-foreground" />;
    case 'leave':
    case 'attendance':
    case 'shifts':
      return <Clock className="size-4 text-warning" />;
    case 'payroll':
      return <FileText className="size-4 text-success" />;
    default:
      return <Bell className="size-4 text-muted-foreground" />;
  }
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const { items, isLoading, loaded, load, markRead, markAllRead } = useAdminNotificationStore();
  const [selectedTab, setSelectedTab] = useState('all');
  const [search, setSearch] = useState('');

  // Load this user's own server notifications (the API scopes them to the caller:
  // per-user rows plus role-broadcasts the user's roles entitle them to).
  useEffect(() => {
    load();
  }, [load]);

  const unreadCount = items.filter((n) => !n.is_read).length;

  const filteredNotifications = useMemo(() => {
    const q = search.toLowerCase().trim();
    return items.filter((n) => {
      const module = notificationModule(n);
      const matchesSearch =
        !q ||
        n.title.toLowerCase().includes(q) ||
        n.message.toLowerCase().includes(q) ||
        module.toLowerCase().includes(q);

      let matchesTab = true;
      if (selectedTab === 'unread') {
        matchesTab = !n.is_read;
      } else if (selectedTab === 'people') {
        matchesTab = PEOPLE_MODULES.includes(module);
      } else if (selectedTab === 'work') {
        matchesTab = ['work', 'support'].includes(module);
      } else if (selectedTab === 'system') {
        matchesTab = module === 'system';
      }

      return matchesSearch && matchesTab;
    });
  }, [items, search, selectedTab]);

  const recruitmentHrCount = useMemo(
    () => items.filter((n) => {
      const m = notificationModule(n);
      return ['recruitment', 'candidates', 'interviews', 'onboarding', 'offboarding'].includes(m);
    }).length,
    [items],
  );

  const handleNotificationClick = (notif: AdminNotification) => {
    if (!notif.is_read) markRead(notif.id);
    const link = notificationLink(notif);
    if (link) navigate(link);
  };

  const handleMarkAllRead = () => {
    markAllRead();
    toast.success('All notifications marked as read');
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="tracking-tight text-foreground flex items-center gap-2.5 font-display text-page-title">
            Notifications Center
            {unreadCount > 0 && (
              <Badge variant="primary">{unreadCount} new</Badge>
            )}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Your activity alerts — onboarding, shift assignments, attendance and leave updates, payroll, and approvals.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead} className="gap-1.5 text-xs">
              <CheckCheck className="size-3.5" /> Mark all read
            </Button>
          )}
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              <Bell className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Total Alerts</p>
              <h3 className="text-foreground font-display text-metric">{items.length}</h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-warning/10 flex items-center justify-center text-warning">
              <AlertCircle className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Unread</p>
              <h3 className="text-foreground font-display text-metric">{unreadCount}</h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-success/10 flex items-center justify-center text-success">
              <Briefcase className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Recruitment & HR</p>
              <h3 className="text-foreground font-display text-metric">{recruitmentHrCount}</h3>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-xs border-border">
          <CardContent className="p-4 flex items-center gap-3.5">
            <div className="size-10 rounded-lg bg-info/10 flex items-center justify-center text-info">
              <ShieldCheck className="size-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">System Status</p>
              <h3 className="text-success font-display text-metric">Active</h3>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main List Card */}
      <Card className="shadow-xs border-border">
        <CardHeader className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            {/* Tabs */}
            <div className="flex flex-wrap gap-1">
              {CATEGORY_TABS.map(tab => (
                <Button
                  key={tab.id}
                  variant={selectedTab === tab.id ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedTab(tab.id)}
                  className="text-xs h-8"
                >
                  {tab.label}
                </Button>
              ))}
            </div>

            {/* Search */}
            <div className="w-full sm:w-64">
              <Input
                leftIcon={<Search className="size-4 text-muted-foreground" />}
                placeholder="Search alerts or modules…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading && !loaded ? (
            <div className="p-12 text-center text-muted-foreground">
              <Loader2 className="size-6 mx-auto mb-2 animate-spin text-muted-foreground/60" />
              <p className="text-sm text-muted-foreground">Loading your notifications…</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Bell className="size-8 mx-auto mb-2 text-muted-foreground/40" />
              <p className="text-sm font-medium text-foreground">No notifications found</p>
              <p className="text-xs text-muted-foreground mt-1">
                You're all caught up! Alerts meant for you will appear here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredNotifications.map((n) => {
                const module = notificationModule(n);
                const link = notificationLink(n);
                return (
                  <div
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`p-4 flex items-start gap-4 transition-colors cursor-pointer hover:bg-secondary/20 group ${
                      !n.is_read ? 'bg-primary/5' : ''
                    }`}
                  >
                    {/* Icon */}
                    <div className="size-9 rounded-lg bg-secondary/70 border border-border flex items-center justify-center shrink-0 mt-0.5 group-hover:border-primary/40 transition-colors">
                      {getModuleIcon(module)}
                    </div>

                    {/* Body */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className={`text-sm tracking-tight ${!n.is_read ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
                          {n.title}
                        </h4>
                        <Badge variant="secondary" className="text-xs font-medium">
                          {MODULE_LABELS[module] || module}
                        </Badge>
                        {!n.is_read && (
                          <span className="size-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {n.message}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span className="tabular-nums">
                          {new Date(n.created_at).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        {link && (
                          <span className="text-primary hover:underline flex items-center gap-1 font-medium">
                            Open details <ExternalLink className="size-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
